import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, Filter, MapPin, Briefcase, Clock, Globe, User, 
  ArrowLeft, ShieldCheck, Eye, Award, GraduationCap, FileText,
  Mail, Phone, Linkedin, ExternalLink, X, Sparkles
} from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const INDUSTRIES = [
  'All Industries', 'Technology', 'Healthcare', 'Finance', 'Education', 
  'Retail', 'Manufacturing', 'Consulting', 'Marketing', 'Real Estate', 'Non-profit'
];

export default function CandidateBrowser({ user }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [minExperience, setMinExperience] = useState(0);
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [total, setTotal] = useState(0);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadProfiles();
  }, [selectedIndustry, selectedLocation, minExperience, remoteOnly]);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('role', searchQuery);
      if (selectedIndustry && selectedIndustry !== 'All Industries') params.append('industry', selectedIndustry);
      if (selectedLocation) params.append('location', selectedLocation);
      if (minExperience > 0) params.append('min_experience', minExperience);
      if (remoteOnly) params.append('remote_only', 'true');
      
      const res = await api.get(`/profiles/search?${params.toString()}`);
      setProfiles(res.data.profiles || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to load candidate profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadProfiles();
  };

  const viewProfile = async (userId) => {
    try {
      const res = await api.get(`/profile/view/${userId}`);
      setSelectedProfile(res.data);
    } catch (error) {
      toast.error('Failed to load profile details');
    }
  };

  const getAvailabilityLabel = (availability) => {
    const labels = {
      'immediate': 'Immediately available',
      '2_weeks': 'Available in 2 weeks',
      '1_month': 'Available in 1 month',
      'exploring': 'Just exploring'
    };
    return labels[availability] || availability;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-2xl font-heading font-bold">Browse Candidates</h1>
                <p className="text-sm text-muted-foreground">{total} candidates found</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Search and Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    type="text"
                    placeholder="Search by role (e.g., Software Engineer, Product Manager)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
                <Button type="submit" data-testid="search-btn">Search</Button>
                <Button type="button" variant="outline" onClick={() => setShowFilters(!showFilters)}>
                  <Filter size={16} className="mr-2" />
                  Filters
                </Button>
              </div>

              {showFilters && (
                <div className="grid md:grid-cols-4 gap-4 pt-4 border-t">
                  <div>
                    <Label>Industry</Label>
                    <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                      <SelectTrigger data-testid="industry-filter">
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDUSTRIES.map(ind => (
                          <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      placeholder="City or Country"
                      value={selectedLocation}
                      onChange={(e) => setSelectedLocation(e.target.value)}
                      data-testid="location-filter"
                    />
                  </div>
                  <div>
                    <Label>Min Experience (years)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="30"
                      value={minExperience}
                      onChange={(e) => setMinExperience(parseInt(e.target.value) || 0)}
                      data-testid="experience-filter"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={remoteOnly}
                        onChange={(e) => setRemoteOnly(e.target.checked)}
                        className="rounded"
                        data-testid="remote-filter"
                      />
                      <span className="text-sm">Open to remote only</span>
                    </label>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        ) : profiles.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <User className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-semibold mb-2">No candidates found</h3>
              <p className="text-muted-foreground">Try adjusting your search filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {profiles.map((profile) => (
              <Card key={profile.user_id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => viewProfile(profile.user_id)} data-testid={`profile-card-${profile.user_id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    {/* Profile Picture */}
                    <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                      {profile.profile_picture ? (
                        <img src={profile.profile_picture} alt={profile.full_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-500 text-white text-xl font-bold">
                          {profile.full_name?.charAt(0) || 'U'}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold truncate">{profile.full_name}</h3>
                        {profile.is_spotlight && (
                          <Badge className="shrink-0 bg-amber-100 text-amber-900 hover:bg-amber-100">
                            <Sparkles size={12} className="mr-1" /> Spotlight
                          </Badge>
                        )}
                        {profile.background_check?.status === 'verified' && (
                          <ShieldCheck size={16} className="text-green-600 flex-shrink-0" title="Background Verified" />
                        )}
                      </div>
                      {profile.headline && (
                        <p className="text-sm text-muted-foreground truncate">{profile.headline}</p>
                      )}
                      {profile.location && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin size={12} /> {profile.location}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {profile.years_experience > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        <Briefcase size={12} className="mr-1" /> {profile.years_experience} yrs exp
                      </Badge>
                    )}
                    {profile.open_to_remote && (
                      <Badge variant="outline" className="text-xs">
                        <Globe size={12} className="mr-1" /> Remote OK
                      </Badge>
                    )}
                    {profile.availability && (
                      <Badge variant="outline" className="text-xs">
                        <Clock size={12} className="mr-1" /> {getAvailabilityLabel(profile.availability)}
                      </Badge>
                    )}
                  </div>

                  {/* Desired Roles */}
                  {profile.desired_roles?.length > 0 && (
                    <div className="mt-3">
                      <div className="flex flex-wrap gap-1">
                        {profile.desired_roles.slice(0, 2).map((role, idx) => (
                          <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                            {role}
                          </span>
                        ))}
                        {profile.desired_roles.length > 2 && (
                          <span className="text-xs text-muted-foreground">+{profile.desired_roles.length - 2} more</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* View Profile Button */}
                  <Button variant="outline" size="sm" className="w-full mt-4" data-testid={`view-profile-btn-${profile.user_id}`}>
                    <Eye size={14} className="mr-2" /> View Profile
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Profile Detail Modal */}
      <Dialog open={!!selectedProfile} onOpenChange={() => setSelectedProfile(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="profile-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Candidate Profile</DialogTitle>
          </DialogHeader>

          {selectedProfile && (
            <div className="space-y-6">
              {/* Header Section */}
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                  {selectedProfile.profile_picture ? (
                    <img src={selectedProfile.profile_picture} alt={selectedProfile.full_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-500 text-white text-3xl font-bold">
                      {selectedProfile.full_name?.charAt(0) || 'U'}
                    </div>
                  )}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold">{selectedProfile.full_name}</h2>
                    {selectedProfile.background_check?.status === 'verified' && (
                      <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                        <ShieldCheck size={16} />
                        Background Verified
                      </div>
                    )}
                  </div>
                  
                  {selectedProfile.headline && (
                    <p className="text-muted-foreground mt-1">{selectedProfile.headline}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                    {selectedProfile.location && (
                      <span className="flex items-center gap-1">
                        <MapPin size={14} /> {selectedProfile.location}
                      </span>
                    )}
                    {selectedProfile.years_experience > 0 && (
                      <span className="flex items-center gap-1">
                        <Briefcase size={14} /> {selectedProfile.years_experience} years experience
                      </span>
                    )}
                    {selectedProfile.profile_views > 0 && (
                      <span className="flex items-center gap-1">
                        <Eye size={14} /> {selectedProfile.profile_views} views
                      </span>
                    )}
                  </div>

                  {/* Contact Info (if available) */}
                  <div className="flex flex-wrap gap-3 mt-3">
                    {selectedProfile.linkedin && (
                      <a href={selectedProfile.linkedin.startsWith('http') ? selectedProfile.linkedin : `https://${selectedProfile.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm flex items-center gap-1">
                        <Linkedin size={14} /> LinkedIn
                      </a>
                    )}
                    {selectedProfile.website && (
                      <a href={selectedProfile.website.startsWith('http') ? selectedProfile.website : `https://${selectedProfile.website}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm flex items-center gap-1">
                        <ExternalLink size={14} /> Website
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Tabs for different sections */}
              <Tabs defaultValue="about">
                <TabsList className="grid grid-cols-4 w-full">
                  <TabsTrigger value="about">About</TabsTrigger>
                  <TabsTrigger value="experience">Experience</TabsTrigger>
                  <TabsTrigger value="resume">Resume</TabsTrigger>
                  <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
                </TabsList>

                <TabsContent value="about" className="space-y-4">
                  {selectedProfile.about_me && (
                    <div>
                      <h4 className="font-semibold mb-2">About</h4>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedProfile.about_me}</p>
                    </div>
                  )}

                  {/* Desired Roles */}
                  {selectedProfile.desired_roles?.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">Desired Roles</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProfile.desired_roles.map((role, idx) => (
                          <Badge key={idx} variant="secondary">{role}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Industries */}
                  {selectedProfile.industries?.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">Industries</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProfile.industries.map((industry, idx) => (
                          <Badge key={idx} variant="outline">{industry}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Availability & Preferences */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 flex items-center gap-2"><Clock size={16} /> Availability</h4>
                      <p className="text-sm">{getAvailabilityLabel(selectedProfile.availability)}</p>
                      <div className="mt-2 space-y-1">
                        {selectedProfile.open_to_remote && (
                          <p className="text-xs text-green-600">✓ Open to remote work</p>
                        )}
                        {selectedProfile.open_to_relocation && (
                          <p className="text-xs text-green-600">✓ Open to relocation</p>
                        )}
                      </div>
                    </div>
                    {selectedProfile.languages?.length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2">Languages</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedProfile.languages.map((lang, idx) => (
                            <Badge key={idx} variant="outline">{lang}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="experience" className="space-y-4">
                  {/* Achievements */}
                  {selectedProfile.achievements?.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center gap-2"><Award size={16} /> Achievements</h4>
                      <ul className="space-y-2">
                        {selectedProfile.achievements.map((achievement, idx) => (
                          <li key={idx} className="text-sm flex items-start gap-2">
                            <span className="text-green-500">✓</span>
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Certifications */}
                  {selectedProfile.certifications?.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center gap-2"><GraduationCap size={16} /> Certifications</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedProfile.certifications.map((cert, idx) => (
                          <Badge key={idx} variant="secondary">{cert}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {(!selectedProfile.achievements?.length && !selectedProfile.certifications?.length) && (
                    <p className="text-muted-foreground text-center py-8">No experience details added yet</p>
                  )}
                </TabsContent>

                <TabsContent value="resume">
                  {selectedProfile.resume ? (
                    <div className="space-y-4">
                      {/* Summary */}
                      {selectedProfile.resume.summary && (
                        <div>
                          <h4 className="font-semibold mb-2">Summary</h4>
                          <p className="text-sm text-muted-foreground">{selectedProfile.resume.summary}</p>
                        </div>
                      )}

                      {/* Experience */}
                      {selectedProfile.resume.experience?.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2">Work Experience</h4>
                          <div className="space-y-3">
                            {selectedProfile.resume.experience.map((exp, idx) => (
                              <div key={idx} className="border-l-2 border-blue-500 pl-4">
                                <h5 className="font-medium">{exp.title}</h5>
                                <p className="text-sm text-muted-foreground">{exp.company}</p>
                                <p className="text-xs text-muted-foreground">{exp.start_date} - {exp.end_date || 'Present'}</p>
                                {exp.description && (
                                  <p className="text-sm mt-1">{exp.description}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Education */}
                      {selectedProfile.resume.education?.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2">Education</h4>
                          <div className="space-y-3">
                            {selectedProfile.resume.education.map((edu, idx) => (
                              <div key={idx} className="border-l-2 border-green-500 pl-4">
                                <h5 className="font-medium">{edu.degree}</h5>
                                <p className="text-sm text-muted-foreground">{edu.school}</p>
                                {edu.graduation_year && (
                                  <p className="text-xs text-muted-foreground">{edu.graduation_year}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Skills */}
                      {selectedProfile.resume.skills?.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2">Skills</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedProfile.resume.skills.map((skill, idx) => (
                              <Badge key={idx} variant="outline">{skill}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="mx-auto text-gray-400 mb-2" size={32} />
                      <p className="text-muted-foreground">No resume attached</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="portfolio">
                  {selectedProfile.portfolio_links?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedProfile.portfolio_links.map((link, idx) => (
                        <a
                          key={idx}
                          href={link.url.startsWith('http') ? link.url : `https://${link.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <ExternalLink size={16} className="text-blue-500" />
                          <div>
                            <p className="font-medium">{link.title}</p>
                            <p className="text-xs text-muted-foreground">{link.url}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">No portfolio links added</p>
                  )}

                  {selectedProfile.video_intro_url && (
                    <div className="mt-4">
                      <h4 className="font-semibold mb-2">Video Introduction</h4>
                      <a
                        href={selectedProfile.video_intro_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-2"
                      >
                        <ExternalLink size={14} /> Watch Video
                      </a>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
